// Worker
import { Worker } from "../Worker";
// Action Creator
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { PlannerPersistedObjectStoreIndexNames, PlannerV2AsyncStorageSchemaVersionV1, ViewStoreTaskEditorRenderModeKey, ViewStoreViewConfigKey } from "@ms/uno-constants/lib/local/PersistenceConstants";
import { RolloutStage } from "@ms/uno-constants/lib/local/RolloutStage";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { StoreEntityState, TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { PersistedObjectStoreSchema } from "@ms/planner-models/lib/local/client/persistence/PersistedAsyncStorageSchema";
import { QueryRelationship } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { convertUnoFiltersToEvoFilters, convertUnoGridColumnWidthsToEvoGridColumnWidths, convertUnoGroupingStatesToEvoGroupingStates, convertUnoSortCriteriaToEvoSortCriteria } from "@ms/planner-models/lib/local/utilities/UnoEvoConversionUtilities";
import { GridViewName } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { extractPrimaryViewIdFromRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { IndexedDBStorage } from "@ms/uno-utilities/lib/local";
import { getCurrentDateWithoutTimestamp } from "@ms/uno-utilities/lib/local/DateUtilities";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { collection, status } from "@ms/uno-utilities/lib/local/storage/TableNameUtilities";
import { deserialize } from "./serialization/SerializationHandler";
export class CacheDataFetchWorker {
    init() {
        if (this.initialized) {
            return;
        }
        // Load Mru data on init
        if (this.configProvider().settings.evoPersistenceSettings.mru && this.configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.App) {
            this.fetchPersistedLeftNavData();
        }
        // Load pinned tasks (local-only task favorites) on init — gated on task persistence
        if (this.configProvider().settings.evoPersistenceSettings.tasks && this.configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.App) {
            if (this.configProvider().flights.EnableNewMyTasksExperience) {
                this.fetchPersistedPinnedTasksData();
                this.fetchPersistedTaskOrderData();
            }
        }
        // For loading data other than Mru
        this.routeStore.subscribeToStore(this.handleRouteStoreChange);
        this.fetchPersistedDataAsPerRouteType();
        // Hydrate global preferences once at init (not on route changes, since they are not route-specific).
        if (this.configProvider().settings.evoPersistenceSettings.view) {
            if (this.configProvider().flights.EnableTaskEditorPanel) {
                this.fetchAndHydrateTaskEditorRenderMode();
            }
        }
        this.initialized = true;
    }
    get name() {
        return Worker.CacheDataFetch;
    }
    /**
     * Stops the worker
     */ stop() {
        this.routeStore.unsubscribeFromStore(this.handleRouteStoreChange);
        this.initialized = false;
    }
    /**
     * Method to read view config from the new localStorage key
     * @param viewId The id of the view
     * @returns The view config resource if found, null otherwise
     */ readViewConfigFromLocalStorage(viewId) {
        if (!this.persistedStorage) {
            return null;
        }
        try {
            const data = this.persistedStorage.getItem(ViewStoreViewConfigKey);
            if (!data) {
                return null;
            }
            const entries = JSON.parse(data);
            const viewIdString = toViewIdString(viewId);
            const entry = entries.find((e)=>e.k === viewIdString);
            if (entry) {
                return {
                    viewId: viewIdString,
                    ...entry.v,
                    taskFilter: JSON.stringify(entry.v.taskFilter)
                };
            }
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e05d65d /* tag_4b3z3 */ , TraceLevel.Warning, `readViewConfigFromLocalStorage parse failure [Error=${error}]`);
        }
        return null;
    }
    /**
     * Method to read view config from Uno persisted storage for backward compatibility
     * We will eventually be able to remove this method once we are sure that all users have the new format of view config persisted
     * @param viewId The id of the view
     */ readViewConfigFromUno(viewId) {
        let config = null;
        const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
        const serializedEmptyArray = JSON.stringify([]);
        if (!this.persistedStorage) {
            this.loggers.traceLogger.logTrace(0x1e0da293 /* tag_4d0kt */ , TraceLevel.Info, `UnoViewConfigReadSkipped`);
            return null;
        }
        try {
            // Filters
            const unoFilters = JSON.parse(this.persistedStorage.getItem(`vstore-tf`) ?? serializedEmptyArray);
            const migratedFilters = convertUnoFiltersToEvoFilters(unoFilters);
            // Group by
            const unoGroupingStates = JSON.parse(this.persistedStorage.getItem(`vstore-tgs`) ?? serializedEmptyArray);
            const migratedGroupingOptions = convertUnoGroupingStatesToEvoGroupingStates(unoGroupingStates);
            // Sort Criteria
            const unoSortCriterias = JSON.parse(this.persistedStorage.getItem(`vstore-sc`) ?? serializedEmptyArray);
            const migratedSortCriteria = convertUnoSortCriteriaToEvoSortCriteria(unoSortCriterias);
            // Grid column widths
            const gridColumnWidths = JSON.parse(this.persistedStorage.getItem(`vstore-gcw`) ?? serializedEmptyArray);
            const migratedGridColumnWidths = convertUnoGridColumnWidthsToEvoGridColumnWidths(gridColumnWidths);
            switch(viewId.name){
                case ViewName.MyDayPage:
                    config = {
                        viewId: toViewIdString(viewId),
                        taskFilter: null,
                        taskGrouping: migratedGroupingOptions.find((g)=>g.k === `2_${userId}`)?.v ?? null,
                        sortCriteria: migratedSortCriteria.find((s)=>s.k === `${GridViewName.MyDayGridView}_${userId}`)?.v ?? null,
                        gridColumnWidths: migratedGridColumnWidths.find((g)=>g.k === `${GridViewName.MyDayGridView}_${userId}`)?.v ?? null,
                        ganttZoomScale: null
                    };
                    break;
                case ViewName.MyTasksPage:
                    const myTasksPageTaskFilter = migratedFilters.find((f)=>f.k === `1_${userId}`)?.v ?? null;
                    config = {
                        viewId: toViewIdString(viewId),
                        taskFilter: myTasksPageTaskFilter ? JSON.stringify(myTasksPageTaskFilter) : null,
                        taskGrouping: migratedGroupingOptions.find((g)=>g.k === `1_${userId}`)?.v ?? null,
                        sortCriteria: migratedSortCriteria.find((s)=>s.k === `${GridViewName.AssignedToUserGridView}_${userId}`)?.v ?? null,
                        gridColumnWidths: migratedGridColumnWidths.find((g)=>g.k === `${GridViewName.AssignedToUserGridView}_${userId}`)?.v ?? null,
                        ganttZoomScale: null
                    };
                    break;
                case ViewName.PlanPage:
                    const planPageTaskFilter = migratedFilters.find((f)=>f.k === `0_${viewId.partitionKey}`)?.v ?? null;
                    config = {
                        viewId: toViewIdString(viewId),
                        taskFilter: planPageTaskFilter ? JSON.stringify(planPageTaskFilter) : null,
                        taskGrouping: migratedGroupingOptions.find((g)=>g.k === `0_${viewId.partitionKey}`)?.v ?? null,
                        sortCriteria: migratedSortCriteria.find((s)=>s.k === `${GridViewName.BasicPlanTasksGridView}_${viewId.partitionKey}`)?.v ?? null,
                        gridColumnWidths: migratedGridColumnWidths.find((g)=>g.k === `${GridViewName.BasicPlanTasksGridView}_${viewId.partitionKey}`)?.v ?? null,
                        ganttZoomScale: null
                    };
                    break;
                default:
            }
        } catch (error) {
            this.loggers.traceLogger.logTrace(0x1e0da25b /* tag_4d0j1 */ , TraceLevel.Warning, `UnoViewConfigParseFailure [Error=${error}]`);
            return null;
        }
        if (config?.taskFilter !== null || config?.taskGrouping !== null || config?.sortCriteria !== null || config?.gridColumnWidths !== null) {
            this.loggers.traceLogger.logTrace(0x1e0db594 /* tag_4d1wu */ , TraceLevel.Info, `UnoViewConfigReadSuccess`);
            return config;
        }
        return null;
    }
    /**
     * Constructor for the CacheDataFetchWorker
     * @param acLoader The action creator loader
     * @param storeLoader The store loader
     * @param persistedStorage persisted storage instance
     * @param persistentAsyncStorage persisted async storage instance
     * @param configProvider The config provider
     * @param loggers The loggers context
     */ constructor(acLoader, storeLoader, persistedStorage, persistentAsyncStorage, configProvider, loggers){
        this.acLoader = acLoader;
        this.storeLoader = storeLoader;
        this.persistedStorage = persistedStorage;
        this.persistentAsyncStorage = persistentAsyncStorage;
        this.configProvider = configProvider;
        this.loggers = loggers;
        /** Indicates if the worker has been initialized */ this.initialized = false;
        /** The route store */ this.routeStore = this.storeLoader.load(Store.Route);
        /**
     * Method to Load Persisted Data from the browser cache as per the current route
     */ this.fetchPersistedDataAsPerRouteType = ()=>{
            const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
            const subRouteType = this.routeStore.getCurrentSubRouteType();
            const currentSubRoute = this.routeStore.getCurrentSubRoute();
            const viewId = extractPrimaryViewIdFromRoute(subRouteType, currentSubRoute, userId);
            // Fetch and hydrate persisted tasks and related data as per the current route type if persistence is enabled for tasks
            if (this.configProvider().settings.evoPersistenceSettings.tasks) {
                switch(subRouteType){
                    case SubRouteType.MyDay:
                        this.fetchPersistedMyDayData(viewId.partitionKey);
                        break;
                    case SubRouteType.MyTasks:
                        this.fetchPersistedMyTasksData(viewId.partitionKey);
                        break;
                    case SubRouteType.MyPlans:
                        break;
                    case SubRouteType.BasicPlan:
                        this.fetchPersistedPlanData(viewId.partitionKey);
                        break;
                    default:
                        break;
                }
            }
            // Fetch and hydrate view config if persistence is enabled for view config
            if (this.configProvider().settings.evoPersistenceSettings.view) {
                this.fetchAndHydrateViewConfig(viewId);
            }
        };
        /**
     * Method to handle route store change events to load the relevant persisted data from cache as
     */ this.handleRouteStoreChange = ()=>{
            this.fetchPersistedDataAsPerRouteType();
        };
        /**
     * Method to hydrate all mru data from the persisted async storage
     */ this.fetchPersistedLeftNavData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.Mru);
                if (items && Object.keys(items).length > 0) {
                    try {
                        const mruItems = [];
                        for (const key of Object.keys(items)){
                            const item = deserialize(TableName.Mru, items[key]);
                            mruItems.push(item);
                        }
                        this.acLoader.load(ActionCreator.CacheDataFetch).hydrateMruData(mruItems);
                    } catch (error) {
                        this.loggers.traceLogger.logTrace(0x1e103049 /* tag_4edbj */ , TraceLevel.Error, `fetchPersistedLeftNavData Deserialization failure [Error=${error}]`);
                    }
                } else {
                    this.loggers.traceLogger.logTrace(0x1e103048 /* tag_4edbi */ , TraceLevel.Info, `No entries in cache [Store=MruStore]`);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e103047 /* tag_4edbh */ , TraceLevel.Error, `fetchPersistedLeftNavData [Error=${error}]`);
            }
        };
        /**
     * Method to hydrate local pinned-task state from IndexedDB into the PinnedTasksStore.
     * Device-local only — no service call is made and no cross-device sync occurs.
     */ this.fetchPersistedPinnedTasksData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.PinnedTasks);
                if (!items || Object.keys(items).length === 0) {
                    return;
                }
                const pinnedTasks = [];
                for (const key of Object.keys(items)){
                    const item = deserialize(TableName.PinnedTasks, items[key]);
                    pinnedTasks.push(item);
                }
                this.acLoader.load(ActionCreator.PinnedTasks).hydrateEntityData(pinnedTasks);
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d89a2cd /* tag_380ln */ , TraceLevel.Error, `fetchPersistedPinnedTasksData [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to hydrate local task-order overrides from IndexedDB into TaskOrderStore.
     * Device-local only — no service call is made and no cross-device sync occurs.
     */ this.fetchPersistedTaskOrderData = async ()=>{
            try {
                const items = await this.persistentAsyncStorage.readAll(TableName.TaskOrder);
                if (!items || Object.keys(items).length === 0) {
                    return;
                }
                const taskOrders = [];
                for (const key of Object.keys(items)){
                    const item = deserialize(TableName.TaskOrder, items[key]);
                    taskOrders.push(item);
                }
                this.acLoader.load(ActionCreator.TaskOrder).hydrateEntityData(taskOrders);
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1d88258e /* tag_38cwo */ , TraceLevel.Error, `fetchPersistedTaskOrderData [Error=${ErrorUtilities.getMessage(error)}]`);
            }
        };
        /**
     * Method to load my day page data from persistent async storage and hydrate the corresponding stores
     */ this.fetchPersistedMyDayData = async (userId)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            // If My Day tasks are already present, no need to fetch from cache
            if (taskStore.getMyDayTasks().status.state === StoreEntityState.Present) {
                return;
            }
            try {
                const [tasksInMyDayStatus, tasks] = await Promise.all([
                    this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksInMyDayOfUser), userId),
                    this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksMyDayDateIndex, getCurrentDateWithoutTimestamp())
                ]);
                if (tasksInMyDayStatus?.state !== StoreEntityState.Present) {
                    this.loggers.traceLogger.logTrace(0x1e103046 /* tag_4edbg */ , TraceLevel.Info, `MyDayPage collection status not present in cache [status=${tasksInMyDayStatus?.state}]`);
                    return;
                }
                if (Object.keys(tasks).length === 0) {
                    this.loggers.traceLogger.logTrace(0x1e103044 /* tag_4edbe */ , TraceLevel.Info, `No myDay tasks in cache [Store=TaskStore]`);
                }
                try {
                    const taskItems = [];
                    for (const key of Object.keys(tasks)){
                        const item = deserialize(TableName.Tasks, tasks[key]);
                        taskItems.push(item);
                    }
                    const deltaLink = tasksInMyDayStatus?.deltaLink ?? undefined;
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateEntityCollectionData(EntityType.Task, QueryRelationship.TasksInMyDayOfUser, this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id, taskItems, deltaLink);
                } catch (error) {
                    this.loggers.traceLogger.logTrace(0x1e103045 /* tag_4edbf */ , TraceLevel.Error, `fetchPersistedMyDayData Deserialization failure [Error=${error}]`);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e103043 /* tag_4edbd */ , TraceLevel.Error, `fetchPersistedMyDayData [Error=${error}]`);
            }
        };
        /**
     * Method to hydrate My Tasks page data from the persisted async storage
     */ this.fetchPersistedMyTasksData = async (pivot)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
            // If ATM tasks are already present, no need to fetch from cache
            if (taskStore.getTasksAssignedToUser(userId).status.state === StoreEntityState.Present) {
                return;
            }
            try {
                if (pivot === MyTasksViewPivotType.AssignedToMe || pivot === MyTasksViewPivotType.All) {
                    const [tasks, taskStatus] = await Promise.all([
                        this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksAssigneeIdentityIdIndex, userId),
                        this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksAssignedToUser), userId)
                    ]);
                    if (taskStatus?.state !== StoreEntityState.Present) {
                        this.loggers.traceLogger.logTrace(0x1e103042 /* tag_4edbc */ , TraceLevel.Info, `MyTasksPage ATM tasks collection status not present in cache [status=${taskStatus?.state}]`);
                        return;
                    }
                    if (Object.keys(tasks).length === 0) {
                        this.loggers.traceLogger.logTrace(0x1e103040 /* tag_4edba */ , TraceLevel.Info, `No ATM tasks in cache [Store=TaskStore]`);
                    }
                    try {
                        const taskItems = [];
                        for (const key of Object.keys(tasks)){
                            const item = deserialize(TableName.Tasks, tasks[key]);
                            taskItems.push(item);
                        }
                        const deltaLink = taskStatus?.deltaLink ?? undefined;
                        this.acLoader.load(ActionCreator.CacheDataFetch).hydrateEntityCollectionData(EntityType.Task, QueryRelationship.TasksAssignedToUser, userId, taskItems, deltaLink);
                    } catch (error) {
                        this.loggers.traceLogger.logTrace(0x1e103041 /* tag_4edbb */ , TraceLevel.Error, `fetchPersistedMyTasksData Deserialization failure [Error=${error}]`);
                    }
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e103023 /* tag_4eda9 */ , TraceLevel.Error, `fetchPersistedMyTasksData [Error=${error}]`);
            }
        };
        /**
     * Method to load plan data from the persisted async storage
     * @param planId id of the plan
     */ this.fetchPersistedPlanData = async (planId)=>{
            const taskStore = this.storeLoader.load(Store.Tasks);
            const isCFDEnabled = this.configProvider().flights.EnableCustomFields;
            // If plan tasks are already present, no need to fetch from cache
            if (taskStore.getTasksForPlan(planId).status.state === StoreEntityState.Present) {
                return;
            }
            try {
                const [plan, planStatus, tasks, tasksInPlanStatus, buckets, bucketInPlanStatus, cfd, cfdInPlanStatus] = await Promise.all([
                    this.persistentAsyncStorage.read(TableName.Plan, planId),
                    this.persistentAsyncStorage.read(status(TableName.Plan), planId),
                    this.persistentAsyncStorage.readByIndex(TableName.Tasks, PlannerPersistedObjectStoreIndexNames.TasksPlanIndex, planId),
                    this.persistentAsyncStorage.read(collection(TableName.Tasks, QueryRelationship.TasksInPlan), planId),
                    this.persistentAsyncStorage.readByIndex(TableName.Bucket, PlannerPersistedObjectStoreIndexNames.BucketsPlanIndex, planId),
                    this.persistentAsyncStorage.read(collection(TableName.Bucket, QueryRelationship.BucketsInPlan), planId),
                    isCFDEnabled ? this.persistentAsyncStorage.readByIndex(TableName.CustomFieldDefinitions, PlannerPersistedObjectStoreIndexNames.CfdPlanIndex, planId) : Promise.resolve([]),
                    isCFDEnabled ? this.persistentAsyncStorage.read(collection(TableName.CustomFieldDefinitions, QueryRelationship.CustomFieldDefinitionsInPlan), planId) : Promise.resolve(undefined)
                ]);
                if (planStatus?.state !== StoreEntityState.Present || tasksInPlanStatus?.state !== StoreEntityState.Present || bucketInPlanStatus?.state !== StoreEntityState.Present || isCFDEnabled && cfdInPlanStatus?.state !== StoreEntityState.Present) {
                    this.loggers.traceLogger.logTrace(0x1e103022 /* tag_4eda8 */ , TraceLevel.Info, `BasicPlanPage collection status not present [Planstatus=${planStatus?.state}][TasksInPlanstatus=${tasksInPlanStatus?.state}][BucketInPlanStatus=${bucketInPlanStatus?.state}]${isCFDEnabled ? `[CfdInPlanStatus=${cfdInPlanStatus?.state}]` : ""}`);
                    return;
                }
                if (!plan) {
                    this.loggers.traceLogger.logTrace(0x1e10218a /* tag_4ecgk */ , TraceLevel.Info, `PlanPage - No entries in cache [Store=PlanStore]`);
                    return;
                }
                try {
                    // deserialize plan
                    const planItem = deserialize(TableName.Plan, plan);
                    // deserialize tasks
                    const taskItems = [];
                    const tasksDeltaLink = tasksInPlanStatus?.deltaLink ?? undefined;
                    if (Object.keys(tasks).length > 0) {
                        for (const key of Object.keys(tasks)){
                            const item = deserialize(TableName.Tasks, tasks[key]);
                            taskItems.push(item);
                        }
                    } else {
                        this.loggers.traceLogger.logTrace(0x1e102189 /* tag_4ecgj */ , TraceLevel.Info, `No plan tasks in cache [Store=TaskStore]`);
                    }
                    // deserialize buckets
                    const bucketItems = [];
                    const bucketsDeltaLink = bucketInPlanStatus?.deltaLink ?? undefined;
                    if (Object.keys(buckets).length > 0) {
                        for (const key of Object.keys(buckets)){
                            const item = deserialize(TableName.Bucket, buckets[key]);
                            bucketItems.push(item);
                        }
                    } else {
                        this.loggers.traceLogger.logTrace(0x1e102188 /* tag_4ecgi */ , TraceLevel.Info, `No plan buckets in cache [Store=BucketStore]`);
                    }
                    const dataFetchAC = this.acLoader.load(ActionCreator.CacheDataFetch);
                    dataFetchAC.hydrateEntityData(EntityType.Plan, planItem);
                    dataFetchAC.hydrateEntityCollectionData(EntityType.Task, QueryRelationship.TasksInPlan, planId, taskItems, tasksDeltaLink);
                    dataFetchAC.hydrateEntityCollectionData(EntityType.Bucket, QueryRelationship.BucketsInPlan, planId, bucketItems, bucketsDeltaLink);
                    // deserialize custom fields for plan
                    if (isCFDEnabled) {
                        const cfdItems = [];
                        const cfdDeltaLink = cfdInPlanStatus?.deltaLink ?? undefined;
                        if (Object.keys(cfd).length > 0) {
                            for (const key of Object.keys(cfd)){
                                const item = deserialize(TableName.CustomFieldDefinitions, cfd[key]);
                                cfdItems.push(item);
                            }
                        } else {
                            this.loggers.traceLogger.logTrace(0x1d8997ca /* tag_38z5k */ , TraceLevel.Info, `No cfd for plan in cache [Store=CFDStore]`);
                        }
                        dataFetchAC.hydrateEntityCollectionData(EntityType.CustomFieldDefinition, QueryRelationship.CustomFieldDefinitionsInPlan, planId, cfdItems, cfdDeltaLink);
                    }
                } catch (error) {
                    this.loggers.traceLogger.logTrace(0x1e102187 /* tag_4ecgh */ , TraceLevel.Error, `Deserialization failure [Error=${error}]`);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e102186 /* tag_4ecgg */ , TraceLevel.Error, `fetchPersistedPlanData [Error=${error}]`);
            }
        };
        /**
     * Method to fetch view config from cache and hydrate the view config store.
     * Reads from (in order): new localStorage key -> IndexedDB -> old localStorage keys (Uno format)
     * @param viewId The id of the view
     */ this.fetchAndHydrateViewConfig = async (viewId)=>{
            try {
                const viewConfig = this.readViewConfigFromLocalStorage(viewId) ?? await this.readViewConfigFromIndexedDB(viewId) ?? this.readViewConfigFromUno(viewId);
                if (viewConfig) {
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateViewConfig(viewId, deserialize(TableName.ViewConfig, viewConfig));
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e0d614d /* tag_4dwfn */ , TraceLevel.Error, `fetchAndHydrateViewConfig [Error=${error}]`);
            }
        };
        /**
     * Method to fetch the global task editor render mode from localStorage and hydrate the store.
     */ this.fetchAndHydrateTaskEditorRenderMode = ()=>{
            if (!this.persistedStorage) {
                return;
            }
            try {
                const data = this.persistedStorage.getItem(ViewStoreTaskEditorRenderModeKey);
                if (data) {
                    const renderMode = JSON.parse(data);
                    this.acLoader.load(ActionCreator.CacheDataFetch).hydrateTaskEditorRenderMode(renderMode);
                }
            } catch (error) {
                this.loggers.traceLogger.logTrace(0x1e053181 /* tag_4btgb */ , TraceLevel.Error, `fetchAndHydrateTaskEditorRenderMode [Error=${error}]`);
            }
        };
        this.readViewConfigFromIndexedDB = async (viewId)=>{
            let viewConfig = await this.persistentAsyncStorage.read(TableName.ViewConfig, toViewIdString(viewId));
            if (viewConfig) {
                return viewConfig;
            }
            // Try to Read from old IndexedDB (persisted async storage)
            // This is only needed in some environments where we need to rev the cache and still retain data stored in view table for eg: filters
            // These environments include dev, demo, dogfood [dev, staging, all] & MSIT
            // We have disabled tasks persistence currently and need to rev cache before enabling it back again
            // ToDo: This code to be cleaned up after some stabilization time to help migrate the view table correctly.
            if (this.configProvider().settings.evoPersistentAsyncStorageVersion > 1) {
                const rolloutStage = this.configProvider().settings.rolloutStage;
                if (rolloutStage === RolloutStage.ProdMSIT || rolloutStage === RolloutStage.DogfoodDev || rolloutStage === RolloutStage.DogfoodStaging || rolloutStage === RolloutStage.DogfoodAll || rolloutStage === RolloutStage.Demo || rolloutStage === RolloutStage.Dev) {
                    const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
                    const oldPersistentAsyncStorage = new IndexedDBStorage(userId, "PlannerV2_1", PlannerV2AsyncStorageSchemaVersionV1, PersistedObjectStoreSchema, this.loggers.traceLogger);
                    viewConfig = await oldPersistentAsyncStorage.read(TableName.ViewConfig, toViewIdString(viewId));
                    if (viewConfig) {
                        this.loggers.traceLogger.logTrace(0x1e05c1df /* tag_4b2h5 */ , TraceLevel.Info, `readViewConfigFromIndexedDB: read view config from old indexedDB`);
                        return viewConfig;
                    }
                }
            }
            return null;
        };
    }
}
